import type { ButtonHTMLAttributes, Ref } from "react";
import "./ModalCloseButton.css";

interface ModalCloseButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "type"> {
  /**
   * What the button dismisses, when "Close" alone would be ambiguous — a close nested inside
   * another dialog, or one of two on the same screen. Announced as "Close {label}".
   *
   * Left off for the ordinary case. A dialog's own heading already says what is open, so
   * "Close modal" adds a word screen-reader users do not need and that sighted users never see.
   */
  label?: string;
  /**
   * Two dialogs focus their close button on open. Declared as an ordinary prop rather than through
   * `forwardRef`: this is React 19, where a function component receives `ref` like any other prop,
   * which is also how `Button` takes it.
   */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The dismiss control in a dialog or panel header.
 *
 * <p>This exists because there were forty-seven of these written by hand. They had drifted into
 * two different glyphs (`×`, the multiplication sign, and `✕`), four naming conventions
 * (`__close`, `__close-button`, `__close-icon`, plus one-offs), two accessible names ("Close" and
 * "Close modal"), and font sizes from 18px to 24px — so the same control was a different size and
 * a different shape depending on which dialog a coach happened to open.</p>
 *
 * <p>Three things it fixes that a copied `&lt;button&gt;` kept getting wrong:</p>
 *
 * <ul>
 *   <li><b>An SVG, not a text glyph.</b> `×` and `✕` are characters, so their rendered size
 *   depended on the inherited font stack and they sat off-centre in a square button. The path
 *   below is drawn to the box and uses `currentColor`, so it scales with the button and inherits
 *   the theme.</li>
 *
 *   <li><b>A real 44px target.</b> Several of the hand-written ones were a 24px glyph with
 *   `padding: 0 4px`, which is a target under half the size a finger needs. The size lives here
 *   so no future dialog has to remember it.</li>
 *
 *   <li><b>A focus ring.</b> Most of the originals set `border: none; background: none` and
 *   nothing else, which in several browsers removes the default focus outline along with the
 *   border — leaving a keyboard user unable to see where they are.</li>
 * </ul>
 *
 * <p>`className` is still forwarded, for the handful of closes that are absolutely positioned over
 * a video or a popover rather than laid out in a header row. Those keep their positioning rule and
 * hand appearance over to this component.</p>
 */
export function ModalCloseButton({ label, className, ...rest }: ModalCloseButtonProps) {
  return (
    <button
      type="button"
      className={className ? `modal-close ${className}` : "modal-close"}
      aria-label={label ? `Close ${label}` : "Close"}
      {...rest}
    >
      <svg
        className="modal-close__glyph"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M4 4l8 8M12 4l-8 8"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
